import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { getConsoleUser } from '@/services/api/user-console';

/**
 * Base key for everything on the user console. Tabs key their per-user queries as
 * [...USER_CONSOLE_QUERY_KEY, pid, ...] (e.g. ['user', pid, 'audit']), so invalidating
 * this key after a header-state mutation (status/segment/restrictions) refreshes the
 * user record AND every dependent tab query — the shared-contract invalidation point.
 */
export const USER_CONSOLE_QUERY_KEY = ['user'] as const;

/**
 * Per-user scope key. Every user-scoped dataset key factory spreads from this —
 * [...userScopeKey(pid), '<dataset>'] — so the invalidation contract above is
 * structural instead of a per-file string convention (four datasets
 * had drifted onto a stray ['users', …] root and escaped scope invalidation).
 */
export const userScopeKey = (pid: string) => [...USER_CONSOLE_QUERY_KEY, pid] as const;

/**
 * Keyed off the RAW route id (pid, fast id, or email). After PID normalization the
 * page redirects and the query re-runs under the canonical key.
 */
export const consoleUserQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...USER_CONSOLE_QUERY_KEY, id],
    queryFn: () => getConsoleUser(id),
  });

export const useConsoleUser = (id: string) => useQuery(consoleUserQueryOptions(id));

/** Refreshes everything held for one user, for a change that shows across several tabs and the header */
export const useInvalidateUserScope = (pid: string) => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: userScopeKey(pid) });
};
